import { Injectable } from "@angular/core";
import { Adapter } from "./adapter";

export interface SubScores {
    allocation: number;
    skills: number;
    staffing: number;
}

export class RobustnessScore {
    constructor(
        public score: number,      // Ex: 46.13
        public createdAt: Date,    // Ex: Date object
        public subScores: SubScores // Ex: { allocation: 33.33, ... }
    ) {}

    private static readonly THRESHOLDS = {
        CRITICAL: 40,
        LOW: 60,
        MEDIUM: 80,
    };

    /**
     * Retourne le niveau de robustesse (Critique, Faible, Moyen, Fort).
     */
    public getRobustnessLevel(): string {
        const score = this.score;
        const T = RobustnessScore.THRESHOLDS;
        
        if (score < T.CRITICAL) {
            return 'Critique';
        } else if (score < T.LOW) {
            return 'Faible';
        } else if (score < T.MEDIUM) {
            return 'Moyen';
        } else {
            return 'Fort';
        }
    }
    
    /**
     * Retourne la classe de couleur Bootstrap (ex: 'text-danger', 'bg-warning') pour un score donné.
     * Cette méthode est statique pour être utilisée facilement sur les sous-scores.
     */
    public static getColorClassForScore(score: number, type: 'text' | 'progress' = 'text'): string {
        const T = RobustnessScore.THRESHOLDS;
        let color = '';
        
        if (score < T.CRITICAL) {
            color = 'danger';
        } else if (score < T.LOW) {
            color = 'warning';
        } else if (score < T.MEDIUM) {
            color = 'info'; // 'info' est souvent utilisé pour le neutre/moyen en Bootstrap
        } else {
            color = 'success';
        }

        return (type === 'progress') ? `bg-${color}` : `text-${color}`;
    }
}

export class RobustnessData {
    public latest!: RobustnessScore;
    public history: RobustnessScore[] = [];
}

// interface for API payload
interface RobustnessScoreApiEntry {
    created_at: string; 
    score: number;
    sub_scores: SubScoresApi
}

export interface RobustnessScoreApiPayload {
    latest: RobustnessScoreApiEntry;
    history: RobustnessScoreApiEntry[];
}

interface SubScoresApi {
    allocation: number;
    skills: number;
    staffing: number;
}

@Injectable({
    providedIn: "root",
})

export class RobustnessAdapter implements Adapter<RobustnessData> {

    adapt(item: RobustnessScoreApiPayload): RobustnessData { // Utilisation de 'any' ou de IroApiPayload
        
        const data = new RobustnessData(); 
        
        if (item.latest) {
            data.latest = this.adaptRobustnessEntry(item.latest);
        }
        
        if (item.history) {
            data.history = item.history.map((entry: RobustnessScoreApiEntry) => this.adaptRobustnessEntry(entry));
        }
        
        return data;
    }
    private adaptRobustnessEntry(item: RobustnessScoreApiEntry): RobustnessScore {
        const createdAt = item.created_at ? new Date(item.created_at) : new Date();
        
        const subScores: SubScores = {
            allocation: item.sub_scores?.allocation ?? 0,
            skills: item.sub_scores?.skills ?? 0,
            staffing: item.sub_scores?.staffing ?? 0
        };
        
        return new RobustnessScore(item.score, createdAt, subScores);
    }
}